// The site's two themes (globals.css): Cherry Night (dark) and Sweet Blush (light). A visitor gets the one their
// device uses until they pick one with the toggle (components/shell/ThemeToggle.tsx), which is then kept in this
// browser. The choice sits in data-theme on <html>, set before the page paints (themeScript in app/layout.tsx).
// The game (game.sweet.xxx) keeps its own dark look.

export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "sweet-theme";

/** Runs in <head> before paint: the stored choice, else the device's, as data-theme. */
export const themeScript = `try{var t=localStorage.getItem("${THEME_STORAGE_KEY}");if(t!=="light"&&t!=="dark")t=matchMedia("(prefers-color-scheme: light)").matches?"light":"dark";if(location.hostname.indexOf("game.")===0)t="dark";document.documentElement.dataset.theme=t}catch(e){document.documentElement.dataset.theme="dark"}`;

const LIGHT_QUERY = "(prefers-color-scheme: light)";

/** The theme the visitor picked in this browser, if any. */
export function storedTheme(): Theme | null {
  try {
    const t = localStorage.getItem(THEME_STORAGE_KEY);
    return t === "light" || t === "dark" ? t : null;
  } catch {
    return null;
  }
}

/** The theme the page shows now. */
export const currentTheme = (): Theme => (document.documentElement.dataset.theme === "light" ? "light" : "dark");

/** Shows a theme; `remember` keeps it as the visitor's choice. */
export function applyTheme(next: Theme, remember: boolean) {
  document.documentElement.dataset.theme = next;
  if (!remember) return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {}
}

/** Calls back when the theme changes; while nothing is picked, follows the device as it changes. */
export function subscribeTheme(onChange: () => void): () => void {
  const watch = new MutationObserver(onChange);
  watch.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  const media = matchMedia(LIGHT_QUERY);
  const follow = () => {
    if (!storedTheme()) applyTheme(media.matches ? "light" : "dark", false);
  };
  media.addEventListener("change", follow);
  return () => {
    watch.disconnect();
    media.removeEventListener("change", follow);
  };
}
